import React, { useEffect, useRef, useState } from 'react';
import { Pencil, Plus } from 'lucide-react';
import { addTag, normalizeTag, removeTag, sortTags, tagKey } from '../services/imagingTags';
import './StudyTags.css';

function sameTagSet(a, b) {
    const ka = sortTags(a).map(tagKey).join('\n');
    const kb = sortTags(b).map(tagKey).join('\n');
    return ka === kb;
}

function TagInput({ inputRef, value, onValueChange, onCommit, onCancel, disabled }) {
    return (
        <form
            className="study-tag-editor"
            onSubmit={(e) => {
                e.preventDefault();
                onCommit();
            }}
        >
            <input
                ref={inputRef}
                type="text"
                value={value}
                maxLength={100}
                placeholder="Tag name"
                aria-label="Tag name"
                disabled={disabled}
                onChange={(e) => onValueChange(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                        e.preventDefault();
                        onCancel();
                    } else if (e.key === 'Enter') {
                        e.preventDefault();
                        onCommit();
                    }
                }}
            />
            <button
                type="submit"
                className="study-tag-editor-button"
                disabled={disabled || !value.trim()}
            >
                Add
            </button>
        </form>
    );
}

function StudyTags({ tags = [], editable = false, onChange, disabled = false, requireConfirmation = false }) {
    const [isEditing, setIsEditing] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [draftTags, setDraftTags] = useState(() => sortTags(tags));
    const [draftValue, setDraftValue] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const inputRef = useRef(null);

    const committed = sortTags(tags);
    const showingDraft = requireConfirmation && isEditing;
    const visible = showingDraft ? draftTags : committed;
    const isDirty = showingDraft && !sameTagSet(draftTags, committed);
    const controlsDisabled = disabled || isSaving;
    const canRemove = editable && (!requireConfirmation || isEditing);

    useEffect(() => {
        if (!isEditing) setDraftTags(sortTags(tags));
    }, [tags, isEditing]);

    useEffect(() => {
        if (isAdding || showingDraft) inputRef.current?.focus();
    }, [isAdding, showingDraft]);

    const applyChange = (next) => {
        if (showingDraft) {
            setDraftTags(sortTags(next));
        } else {
            onChange?.(next);
        }
    };

    const commitDraftValue = () => {
        const value = normalizeTag(draftValue);
        setDraftValue('');
        if (!value) {
            if (!showingDraft) setIsAdding(false);
            return;
        }
        const next = addTag(visible, value);
        if (next.length !== visible.length) applyChange(next);
        if (!showingDraft) setIsAdding(false);
    };

    const cancelDraftValue = () => {
        setDraftValue('');
        if (!showingDraft) setIsAdding(false);
    };

    const startEditing = () => {
        setDraftTags(committed);
        setDraftValue('');
        setIsEditing(true);
    };

    const cancelEditing = () => {
        setDraftValue('');
        setIsEditing(false);
    };

    const saveEditing = async () => {
        const pending = normalizeTag(draftValue);
        const finalTags = pending ? addTag(draftTags, pending) : draftTags;
        if (sameTagSet(finalTags, committed)) {
            cancelEditing();
            return;
        }
        setIsSaving(true);
        try {
            await onChange?.(finalTags);
        } finally {
            setIsSaving(false);
            setDraftValue('');
            setIsEditing(false);
        }
    };

    return (
        <div className="study-tags" onClick={(e) => e.stopPropagation()}>
            {visible.map((tag) => (
                <span key={tagKey(tag)} className="study-tag" title={tag}>
                    {tag}
                    {canRemove && (
                        <button
                            type="button"
                            className="study-tag-remove"
                            aria-label={`Remove tag ${tag}`}
                            disabled={controlsDisabled}
                            onClick={() => applyChange(removeTag(visible, tag))}
                        >
                            ×
                        </button>
                    )}
                </span>
            ))}

            {!visible.length && !editable && (
                <span className="study-tags-empty">No tags</span>
            )}

            {editable && requireConfirmation && !isEditing && (
                <button
                    type="button"
                    className="study-tag-add-button"
                    disabled={controlsDisabled}
                    onClick={startEditing}
                >
                    <Pencil size={12} strokeWidth={2.5} />
                    Edit tags
                </button>
            )}

            {editable && !requireConfirmation && !isAdding && (
                <button
                    type="button"
                    className="study-tag-add-button"
                    disabled={controlsDisabled}
                    onClick={() => setIsAdding(true)}
                >
                    <Plus size={12} strokeWidth={2.5} />
                    Add tag
                </button>
            )}

            {editable && (isAdding || showingDraft) && (
                <TagInput
                    inputRef={inputRef}
                    value={draftValue}
                    onValueChange={setDraftValue}
                    onCommit={commitDraftValue}
                    onCancel={showingDraft ? cancelEditing : cancelDraftValue}
                    disabled={controlsDisabled}
                />
            )}

            {editable && isAdding && !showingDraft && (
                <button
                    type="button"
                    className="study-tag-editor-button secondary"
                    onClick={cancelDraftValue}
                >
                    Cancel
                </button>
            )}

            {showingDraft && (
                <div className="study-tags-confirm">
                    <button
                        type="button"
                        className="study-tag-editor-button"
                        disabled={controlsDisabled || (!isDirty && !draftValue.trim())}
                        onClick={saveEditing}
                    >
                        {isSaving ? 'Saving…' : 'Save tags'}
                    </button>
                    <button
                        type="button"
                        className="study-tag-editor-button secondary"
                        disabled={isSaving}
                        onClick={cancelEditing}
                    >
                        Cancel
                    </button>
                </div>
            )}
        </div>
    );
}

export default StudyTags;
